/* The table power features: bulk bar, filter chips, view tabs, tools, column menu, quick-peek.
   Tokens only - no colour literals (ADR-0007). */

.pop {
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
}

/* ---- buttons the tools share ------------------------------------------------------------- */

.tool,
.primary,
.addFilter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: var(--h-ctl);
  padding: 0 10px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-chip);
  background: var(--panel);
  color: var(--ink-2);
  font: inherit;
  font-size: var(--t-dense);
  white-space: nowrap;
  cursor: pointer;
}

.tool:hover,
.addFilter:hover {
  border-color: var(--accent-line);
  background: var(--accent-w);
  color: var(--accent);
}

.primary {
  border-color: var(--accent);
  background: var(--accent);
  color: var(--on-fill);
  font-weight: 650;
}

.primary:hover {
  background: var(--accent-h);
}

.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.addFilter {
  border-style: dashed;
  color: var(--accent);
  font-weight: 600;
}

.iconTool {
  display: inline-grid;
  place-items: center;
  width: var(--h-ctl);
  height: var(--h-ctl);
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--radius-chip);
  background: none;
  color: var(--ink-2);
  cursor: pointer;
}

.iconTool:hover {
  background: var(--panel-3);
}

.tool:focus-visible,
.primary:focus-visible,
.addFilter:focus-visible,
.iconTool:focus-visible,
.tab:focus-visible,
.item:focus-visible,
.chipMain:focus-visible,
.chipRemove:focus-visible,
.clearBulk:focus-visible,
.colTrigger:focus-visible,
.linkButton:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.badge {
  min-width: 18px;
  padding: 0 5px;
  border-radius: var(--radius-pill);
  background: var(--accent);
  color: var(--on-fill);
  font-size: var(--t-min);
  line-height: 18px;
  text-align: center;
}

.tools {
  display: inline-flex;
  flex: 0 1 auto;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

/* ---- the panel a trigger opens ------------------------------------------------------------ */

/* A long option list: the search box stays at the top and the options scroll beneath it. */
.listScroll {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
}

.list:has(> .listSearch) {
  overflow-y: hidden;
}

.listSearch {
  flex: none;
  box-sizing: border-box;
  width: 100%;
  min-height: var(--target-min);
  margin-bottom: 4px;
  padding: 0 8px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
  background: var(--panel);
  color: var(--ink);
  font: inherit;
  font-size: var(--t-dense);
}

.listSearch:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 1px;
}

.listEmpty {
  padding: 8px;
  color: var(--muted);
  font-size: var(--t-dense);
}

.panel,
.list {
  position: absolute;
  z-index: 40;
  top: calc(100% + 6px);
  left: 0;
  display: flex;
  flex-direction: column;
  min-width: 200px;
  max-width: min(320px, 90vw);
  max-height: 320px;
  padding: 4px;
  overflow-y: auto;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-card);
  background: var(--panel);
  box-shadow: var(--shadow-pop);
}

.panelRight {
  right: 0;
  left: auto;
}

.panel:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}

.item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 5px 8px;
  border: 0;
  border-radius: var(--radius-ctl);
  background: none;
  color: var(--ink);
  font: inherit;
  font-size: var(--t-dense);
  text-align: left;
  cursor: pointer;
}

.item:hover {
  background: var(--panel-2);
}

.item:focus-visible {
  outline-offset: -2px;
  background: var(--accent-w);
}

.item[aria-checked="true"],
.item[aria-selected="true"] {
  font-weight: 650;
}

.itemLabel {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.itemHint {
  color: var(--muted);
  font-size: var(--t-min);
  font-variant-numeric: lining-nums tabular-nums;
}

.itemTick {
  display: grid;
  flex: 0 0 14px;
  place-items: center;
  color: var(--accent);
}

.chooser {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
}

.choice {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  color: var(--ink);
  font-size: var(--t-dense);
}

.choice input {
  accent-color: var(--accent);
}

.linkButton {
  align-self: flex-start;
  padding: 4px 0;
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 650;
  cursor: pointer;
}

.exportLine {
  margin: 0;
  color: var(--ink-2);
  font-size: var(--t-dense);
}

/* ---- filter chips ------------------------------------------------------------------------- */

.chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.chipOn {
  display: inline-flex;
  align-items: center;
  height: var(--h-ctl);
  border: 1px solid var(--accent-line);
  border-radius: var(--radius-pill);
  background: var(--accent-w);
  color: var(--accent);
  font-size: var(--t-dense);
  font-weight: 650;
}

.chipMain,
.chipRemove {
  display: inline-flex;
  align-items: center;
  height: 100%;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.chipMain {
  padding: 0 4px 0 12px;
  border-radius: var(--radius-pill) 0 0 var(--radius-pill);
}

.chipRemove {
  padding: 0 9px 0 4px;
  border-radius: 0 var(--radius-pill) var(--radius-pill) 0;
}

.chipMain:hover,
.chipRemove:hover {
  background: var(--accent-line);
}

/* ---- bulk bar ----------------------------------------------------------------------------- */

.bulk {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  min-height: calc(var(--h-ctl) + 2px);
}

.bulkCount {
  color: var(--accent);
  font-size: var(--t-dense);
  font-weight: 700;
  font-variant-numeric: lining-nums tabular-nums;
}

.sep {
  align-self: stretch;
  width: 1px;
  background: var(--line-2);
}

.bulkActions {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.clearBulk {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: var(--h-ctl);
  padding: 0 8px;
  border: 0;
  border-radius: var(--radius-ctl);
  background: none;
  color: var(--accent);
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 650;
  cursor: pointer;
}

.clearBulk:hover {
  background: var(--accent-w);
}

.selectAll {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--accent-line);
  background: var(--accent-w);
  color: var(--ink);
  font-size: var(--t-dense);
}

/* ---- saved view tabs ---------------------------------------------------------------------- */

.views {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 12px;
  margin: -4px 0 8px;
  border-bottom: 1px solid var(--line);
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: none;
  color: var(--ink-2);
  font: inherit;
  font-size: var(--t-dense);
  cursor: pointer;
}

.tab:hover {
  color: var(--accent);
}

.tabOn {
  border-bottom-color: var(--accent);
  color: var(--accent);
  font-weight: 650;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--warn);
}

.star {
  color: var(--accent);
}

.viewActions,
.nameForm {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 4px;
}

.nameForm input {
  height: var(--h-ctl);
  padding: 0 10px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-chip);
  background: var(--panel);
  color: var(--ink);
  font: inherit;
  font-size: var(--t-dense);
}

/* ---- column menu -------------------------------------------------------------------------- */

.colTrigger {
  display: inline-grid;
  place-items: center;
  width: var(--target-min);
  height: var(--target-min);
  padding: 0;
  border: 0;
  border-radius: var(--radius-ctl);
  background: none;
  color: var(--muted);
  opacity: 0;
  cursor: pointer;
}

.colTrigger:hover,
.colTrigger[aria-expanded="true"] {
  background: var(--panel-2);
  color: var(--ink);
}

/* ---- quick-peek ---------------------------------------------------------------------------- */

.peek {
  position: fixed;
  z-index: 50;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: min(380px, 100vw);
  border-left: 1px solid var(--line);
  background: var(--panel);
  box-shadow: var(--shadow-drawer);
}

.peekHead {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid var(--line);
}

.peekTitle {
  flex: 1;
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
  font-size: var(--t-head);
  font-weight: 650;
}

.peekBody {
  flex: 1;
  padding: 14px 16px;
  overflow-y: auto;
}

/* ---- shortcut sheet ------------------------------------------------------------------------ */

.shortcuts {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
}

.shortcut {
  display: grid;
  grid-template-columns: 9.5rem 1fr;
  gap: 10px;
  align-items: baseline;
}

.shortcut dt,
.shortcut dd {
  margin: 0;
  font-size: var(--t-dense);
}

.shortcut kbd {
  padding: 2px 6px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
  background: var(--panel-2);
  font: inherit;
  font-size: var(--t-min);
  white-space: nowrap;
}

@media (width <= 900px) {
  .tool,
  .primary,
  .addFilter,
  .iconTool,
  .chipOn,
  .clearBulk {
    height: var(--target-touch);
  }
}
